import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserDetailResponse } from '../../api/generated';
import { InputField, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import modalStyles from '../UI/Modal.module.css';
import { LocationColorPicker } from './LocationColorPicker';
import { LocationCopySection } from './LocationCopySection';
import type { CopySourceOption } from './LocationCopySection';
import { LocationUserRolesGrid } from './LocationUserRolesGrid';
import { LOCATION_FORM_MODAL_WIDTH } from './locationsConstants';
import { DEFAULT_LOCATION_COLOR, type LocationColorId } from '../../constants/locationColors';
import { LOCATION_ROLES, type LocationRole } from '../../constants/locationRoles';
import { withRoleToggled } from '../../services/locationUserService';
import type { LocationUserRoles } from '../../services/locationUserService';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import type { LocationSettingsRow, LocationSettingsValues } from '../../hooks/useLocationsSettings';
import { isNameTaken } from '../../utils/stringUtils';
import styles from './LocationFormModal.module.css';

interface LocationFormModalProps {
  location: LocationSettingsRow | null;
  users: UserDetailResponse[];
  // Names held by the other locations, so a new or renamed location cannot
  // take one that is already in use. The row being edited is left out by the
  // caller, which is what lets a rename keep its own current name.
  takenNames: string[];
  // True when the user list could not be loaded. The roles grid is hidden
  // rather than rendered empty, so a failed fetch is never mistaken for "this
  // location has no users".
  hasUsersLoadError: boolean;
  // True while the user list is still being fetched. The roles grid shows a
  // loading note and Save is disabled, because saving a grid seeded from an
  // unloaded user list would write "no roles" over roles users really hold.
  // Also true briefly mid-save on create, when the baseline is refetched.
  isUsersLoading: boolean;
  // Every existing location, offered as a source to copy from when creating.
  // Empty hides the copy section entirely: there is nothing to copy from.
  copySources: CopySourceOption[];
  // Meaningful only when creating. The caller drops it on edit rather than
  // relying on it being null: the failed-rights retry reopens this same modal
  // as an edit with the chosen source still in state.
  onSave: (
    values: LocationSettingsValues,
    copyFromLocationId: string | null
  ) => void | Promise<void>;
  onClose: () => void;
}

interface LocationFormState {
  name: string;
  colorId: LocationColorId;
  userRoles: LocationUserRoles[];
}

// Creating assigns the signed-in administrator the planner role on the new
// location, which the backend does inside the create call itself. Seeding that
// tick means the grid shows what will actually be true rather than an empty row
// for the one user guaranteed to end up with access.
const buildCreatorRoles = (currentUserId: string | null): LocationUserRoles[] =>
  currentUserId ? [{ userId: currentUserId, roles: [LOCATION_ROLES.Planner] }] : [];

const buildInitialState = (
  location: LocationSettingsRow | null,
  currentUserId: string | null
): LocationFormState => ({
  name: location?.name ?? '',
  colorId: location?.colorId ?? DEFAULT_LOCATION_COLOR.id,
  userRoles: location?.userRoles ?? buildCreatorRoles(currentUserId)
});

export const LocationFormModal = ({
  location,
  users,
  takenNames,
  hasUsersLoadError,
  isUsersLoading,
  copySources,
  onSave,
  onClose
}: LocationFormModalProps) => {
  const { t } = useTranslation();
  const { id: currentUserId } = useCurrentUser();
  const isEditing = location !== null;
  const [form, setForm] = useState<LocationFormState>(() =>
    buildInitialState(location, currentUserId)
  );
  // Held apart from `form`, which mirrors the location's own values. The copy
  // choice is not one of those: it says how the location is seeded and then
  // stops existing.
  const [copyFromLocationId, setCopyFromLocationId] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  // The form seeds from the row as it was when Edit was clicked. If the users
  // fetch was still running then, the seeded roles are empty — so when the
  // fetch settles the roles are seeded again from the fresh row. The grid is
  // hidden while loading, so there are no ticks to overwrite.
  //
  // Seeded only when the modal opened DURING a load, which is the only case with
  // nothing to seed from. A modal opened against a loaded list starts correct and
  // must never be re-seeded: the create flow refetches users mid-save for a clean
  // diff baseline, and re-seeding on that would silently reset the grid to the
  // creator's own role — discarding the ticks the administrator is about to retry
  // with, so the retry would write nothing at all.
  const wasUsersLoadingRef = useRef(isUsersLoading);
  const needsSeedOnLoadRef = useRef(isUsersLoading);
  useEffect(() => {
    if (wasUsersLoadingRef.current && !isUsersLoading && needsSeedOnLoadRef.current) {
      needsSeedOnLoadRef.current = false;
      setForm((prev) => ({
        ...prev,
        userRoles: location?.userRoles ?? buildCreatorRoles(currentUserId)
      }));
    }
    wasUsersLoadingRef.current = isUsersLoading;
  }, [isUsersLoading, location, currentUserId]);

  // Holding at least one role is what makes a location visible to a user, and
  // administering the account is not a bypass here — so an administrator who
  // clears their own last role loses the location from their list with no way
  // back except another administrator. The grid disables that one box; this
  // rejects the toggle even if it is reached another way.
  //
  // Only blocks removing the LAST one. Assigning yourself a role is always
  // allowed, so this can never strand an administrator on a location they can
  // see but not give themselves access to.
  const isSelfLockout = (userId: string, role: LocationRole): boolean => {
    if (userId !== currentUserId) return false;
    const held = form.userRoles.find((entry) => entry.userId === userId)?.roles ?? [];
    return held.length === 1 && held[0] === role;
  };

  const toggleRole = (userId: string, role: LocationRole): void => {
    if (isSelfLockout(userId, role)) return;
    setForm((prev) => {
      const hasEntry = prev.userRoles.some((entry) => entry.userId === userId);
      if (!hasEntry) {
        // Seeded before this user was known, so start them from no roles and
        // assign the one just ticked.
        return { ...prev, userRoles: [...prev.userRoles, { userId, roles: [role] }] };
      }
      return {
        ...prev,
        userRoles: prev.userRoles.map((entry) =>
          entry.userId === userId ? { ...entry, roles: withRoleToggled(entry.roles, role) } : entry
        )
      };
    });
  };

  const renderUsersSection = (): ReactNode => {
    if (isUsersLoading) {
      return <p className={styles.emptyNote}>{t('config-loc-users-loading')}</p>;
    }
    if (hasUsersLoadError) {
      return <p className={styles.emptyNote}>{t('config-loc-users-load-error')}</p>;
    }
    if (users.length === 0) {
      return <p className={styles.emptyNote}>{t('config-loc-no-users')}</p>;
    }
    return (
      <>
        <p className={styles.sectionHint}>{t('config-loc-users-hint')}</p>
        <LocationUserRolesGrid
          users={users}
          assignments={form.userRoles}
          onToggle={toggleRole}
          lockedUserId={currentUserId}
        />
      </>
    );
  };

  // Returns the onSave promise so the Save button stays disabled until the
  // per-user role writes settle.
  const handleSave = (): void | Promise<void> => {
    if (!form.name.trim()) {
      setNameError(t('config-loc-error-name'));
      return;
    }
    if (isNameTaken(form.name, takenNames)) {
      setNameError(t('config-loc-error-name-taken'));
      return;
    }
    setNameError(undefined);
    return onSave(form, copyFromLocationId);
  };

  return (
    <Modal
      title={isEditing ? t('config-loc-edit') : t('config-loc-create')}
      onClose={onClose}
      width={LOCATION_FORM_MODAL_WIDTH}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          {/* Gated in BOTH modes: saving a grid seeded from a list that never
              loaded would write "no roles" over roles users really hold, and
              on create it would make a permanent location with no one assigned
              at all while reporting success. */}
          <Button
            type="primary"
            onClick={handleSave}
            disabled={isUsersLoading || hasUsersLoadError}
          >
            {t('config-iv-save')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <div className={styles.identityRow}>
          <InputField
            label={t('config-loc-name')}
            required
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={nameError}
            className={styles.nameField}
          />
          <LocationColorPicker
            colorId={form.colorId}
            onSelect={(colorId) => setForm((f) => ({ ...f, colorId }))}
          />
        </div>

        {/* Create only. Cloning happens once, when the location is empty, which
            is what keeps the import pull-only: it can never overwrite data
            somebody is already working in. */}
        {!isEditing && copySources.length > 0 && (
          <LocationCopySection
            sourceLocationId={copyFromLocationId}
            sources={copySources}
            onChange={setCopyFromLocationId}
          />
        )}

        <div className={styles.usersSection}>
          <span className={styles.sectionLabel}>{t('config-loc-user-roles')}</span>

          {renderUsersSection()}
        </div>
      </div>
    </Modal>
  );
};
